import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout.jsx';
import { trackerApi } from '../services/projectTrackerApi.js';
import './MisTracker.css';

const FALLBACK_STATUSES = ['Not Started', 'WIP', 'On Hold', 'Completed'];

const HEALTH = {
  Red: { label: 'Overdue', tone: 'red' },
  Amber: { label: 'Due soon', tone: 'amber' },
  Green: { label: 'On track', tone: 'green' },
  Blue: { label: 'On hold', tone: 'blue' },
  Grey: { label: 'Not started', tone: 'grey' },
};

// <input type="date"> wants YYYY-MM-DD. The API sends DATE columns as ISO
// timestamps at local midnight, so read them back with local date parts.
const toInputDate = (value) => {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const formatDate = (value) => {
  const iso = toInputDate(value);
  if (!iso) return '-';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const draftFrom = (project) => ({
  status: project.status || 'Not Started',
  issue: project.issue || '',
  revised_target_date: toInputDate(project.revised_target_date),
  actual_closure_date: toInputDate(project.actual_closure_date),
});

const isCompleted = (status) => String(status || '').toLowerCase() === 'completed';

const MisTracker = () => {
  const [projects, setProjects] = useState([]);
  const [statuses, setStatuses] = useState(FALLBACK_STATUSES);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [notice, setNotice] = useState(null);

  const showNotice = (type, message) => {
    setNotice({ type, message });
    window.setTimeout(() => setNotice(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await trackerApi('/mine');
      const list = Array.isArray(data?.projects) ? data.projects : [];
      setProjects(list);
      if (Array.isArray(data?.statuses) && data.statuses.length) setStatuses(data.statuses);
      setDrafts(Object.fromEntries(list.map((p) => [p.id, draftFrom(p)])));
    } catch (err) {
      setLoadError(err.response?.data?.message || 'Your tracker projects could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const summary = useMemo(() => ({
    total: projects.length,
    wip: projects.filter((p) => String(p.status).toLowerCase() === 'wip').length,
    completed: projects.filter((p) => isCompleted(p.status)).length,
    overdue: projects.filter((p) => p.health === 'Red').length,
  }), [projects]);

  const setField = (id, field, value) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const isDirty = (project) => {
    const draft = drafts[project.id];
    if (!draft) return false;
    const base = draftFrom(project);
    return Object.keys(base).some((key) => (draft[key] || '') !== (base[key] || ''));
  };

  const save = async (project) => {
    const draft = drafts[project.id];
    const payload = {
      status: draft.status,
      issue: draft.issue,
      revised_target_date: draft.revised_target_date,
    };
    if (isCompleted(draft.status)) payload.actual_closure_date = draft.actual_closure_date;

    setSavingId(project.id);
    try {
      const data = await trackerApi(`/mine/${project.id}`, { method: 'PUT', body: payload });
      const updated = data?.project;
      if (updated) {
        setProjects((prev) => prev.map((p) => (p.id === project.id ? updated : p)));
        setDrafts((prev) => ({ ...prev, [project.id]: draftFrom(updated) }));
      }
      showNotice('success', `${project.project_name} updated - the admin tracker now shows this.`);
    } catch (err) {
      showNotice('danger', err.response?.data?.message || 'The update could not be saved.');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Layout>
      <section className="mis-tracker">
        {notice && <div className={`alert alert-${notice.type} mis-alert`} role="alert">{notice.message}</div>}

        <header className="mis-head">
          <div>
            <h1>MIS Tracker</h1>
            <p>Projects you own. Update status, progress notes and dates here - the admin tracker reflects every change straight away.</p>
          </div>
          <button type="button" className="mis-refresh" onClick={load} disabled={loading}>
            <i className="bi bi-arrow-clockwise"></i> Refresh
          </button>
        </header>

        {!loading && !loadError && projects.length > 0 && (
          <div className="mis-summary">
            <div><strong>{summary.total}</strong><span>My projects</span></div>
            <div><strong>{summary.wip}</strong><span>In progress</span></div>
            <div><strong>{summary.completed}</strong><span>Completed</span></div>
            <div className={summary.overdue ? 'is-alert' : ''}><strong>{summary.overdue}</strong><span>Overdue</span></div>
          </div>
        )}

        {loading && <div className="mis-empty"><div className="spinner-border text-success" role="status"></div><p>Loading your projects…</p></div>}

        {!loading && loadError && (
          <div className="mis-empty">
            <i className="bi bi-exclamation-triangle"></i>
            <p>{loadError}</p>
            <button type="button" className="mis-refresh" onClick={load}>Try again</button>
          </div>
        )}

        {!loading && !loadError && projects.length === 0 && (
          <div className="mis-empty">
            <i className="bi bi-kanban"></i>
            <p>No tracker projects are assigned to you.</p>
            <small>If you should own one, ask an admin to set you as the project owner in the tracker.</small>
          </div>
        )}

        <div className="mis-list">
          {projects.map((project) => {
            const draft = drafts[project.id] || draftFrom(project);
            const health = HEALTH[project.health] || HEALTH.Grey;
            const options = statuses.includes(draft.status) ? statuses : [draft.status, ...statuses];
            const dirty = isDirty(project);
            const saving = savingId === project.id;
            return (
              <article className="mis-card" key={project.id}>
                <div className="mis-card-head">
                  <div>
                    <h2>{project.project_name}</h2>
                    <p>
                      {project.client}
                      {project.project_code ? <span className="mis-code">{project.project_code}</span> : null}
                    </p>
                  </div>
                  <div className="mis-badges">
                    {project.priority && <span className={`mis-pill priority-${String(project.priority).toLowerCase()}`}>{project.priority}</span>}
                    <span className={`mis-pill health-${health.tone}`}>
                      {health.label}
                      {project.health === 'Red' && project.delay_days > 0 ? ` · ${project.delay_days}d late` : ''}
                    </span>
                  </div>
                </div>

                <div className="mis-fields">
                  <label>Status
                    <select value={draft.status} onChange={(e) => setField(project.id, 'status', e.target.value)} disabled={saving}>
                      {options.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </label>
                  <label>Original target
                    <input type="text" value={formatDate(project.original_target_date)} readOnly disabled />
                  </label>
                  <label>Revised target
                    <input type="date" value={draft.revised_target_date} onChange={(e) => setField(project.id, 'revised_target_date', e.target.value)} disabled={saving} />
                  </label>
                  {isCompleted(draft.status) && (
                    <label>Closed on
                      <input type="date" value={draft.actual_closure_date} onChange={(e) => setField(project.id, 'actual_closure_date', e.target.value)} disabled={saving} />
                    </label>
                  )}
                </div>

                <label className="mis-note">Update / issue
                  <textarea
                    rows={3}
                    maxLength={5000}
                    value={draft.issue}
                    placeholder="What's the latest on this project? Blockers, next steps…"
                    onChange={(e) => setField(project.id, 'issue', e.target.value)}
                    disabled={saving}
                  />
                </label>

                <div className="mis-card-foot">
                  <small>Last updated {formatDate(project.updated_at)}</small>
                  <button type="button" className="mis-save" onClick={() => save(project)} disabled={!dirty || saving}>
                    {saving ? 'Saving…' : 'Save update'}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </Layout>
  );
};

export default MisTracker;
